import { useMutation, useQueryClient } from "@tanstack/react-query";

import { deleteMerchantsPublicIDEmployeesEmployeePublicID } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { merchantEmployeeKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployee";
import { merchantEmployeesKeys } from "Api/Queries/Merchants/Employees/useMerchantEmployees";
import { toast } from "sonner";
import { useResource, Resources } from "Translations/Resources";

const RESOURCES = Resources.Hooks.Employee;

type Parameters = {
  publicID: string;
  employeePublicID: string;
};

type Props = {
  onSuccess?: () => void;
  onError?: () => void;
};

/**
 * Removes the employee from the merchant. Nothing is deleted: the record turns Inactive and the
 * person's employee access is suspended, so the same employee can be invited back later.
 */
export const useRemoveEmployeeMutation = ({
  onSuccess,
  onError,
}: Props = {}) => {
  const queryClient = useQueryClient();
  const { t } = useResource();

  return useMutation({
    mutationFn: processResponseWithApiProblemDetails(
      ({ publicID, employeePublicID }: Parameters) =>
        deleteMerchantsPublicIDEmployeesEmployeePublicID(
          publicID,
          employeePublicID,
        ),
    ),
    onSuccess: (_: unknown, { publicID, employeePublicID }: Parameters) => {
      queryClient.invalidateQueries({
        queryKey: merchantEmployeesKeys.lists(),
      });
      queryClient.invalidateQueries({
        queryKey: merchantEmployeeKeys.singleEmployee(
          publicID,
          employeePublicID,
        ),
      });
      toast.success(t(RESOURCES.EmployeeRemoved));
      onSuccess?.();
    },
    onError: () => {
      onError?.();
      toast.error(t(RESOURCES.EmployeeRemoveError));
    },
  });
};
